import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ToastProvider } from "@/lib/toast";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Bids from "@/pages/Bids";
import Pipeline from "@/pages/Pipeline";
import Feed from "@/pages/Feed";
import Documents from "@/pages/Documents";
import Proposals from "@/pages/Proposals";
import Assistant from "@/pages/Assistant";
import Emails from "@/pages/Emails";
import EmailTemplates from "@/pages/EmailTemplates";
import Team from "@/pages/Team";
import HRPayroll from "@/pages/HRPayroll";
import Profile from "@/pages/Profile";
import DMS from "@/pages/DMS";
import Suppliers from "@/pages/Suppliers";
import PriceList from "@/pages/PriceList";
import CompanySettings from "@/pages/CompanySettings";
import Tickets from "@/pages/Tickets";
import BidWizard from "@/pages/BidWizard";
import CustomerPortal from "@/pages/CustomerPortal";

// Root route routing helper — customers land on the Portal, everyone else on Dashboard
function HomeRouter() {
  const { user } = useAuth();
  if (user?.role === "customer") return <Navigate to="/portal/tickets" replace />;
  return <Dashboard />;
}

// Wrapper for admin/team-only pages: customers get redirected to the portal
// so they never see the internal /tickets, /bids, /dms etc.
function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center muted text-sm">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/portal/tickets" replace />;
  return <Layout>{children}</Layout>;
}

// Wrapper for the customer portal — non-customers redirected out
function PortalOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center muted text-sm">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Admins/managers can view the portal too (useful for testing/debugging) —
  // if you want strict customer-only, uncomment the next line:
  // if (user.role !== "customer") return <Navigate to="/" replace />;
  return children;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center muted text-sm">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

const P = ({ children }) => <Protected>{children}</Protected>;
const A = ({ children }) => <AdminOnly>{children}</AdminOnly>;

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  // Emergent Google sign-in returns to {origin}/#session_id=... — detect the
  // fragment synchronously during render (NOT in useEffect) so the callback
  // wins the race against ProtectedRoute/AuthProvider.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />

            {/* Customer-facing portal (minimal chrome, no admin sidebar) */}
            <Route path="/portal/tickets" element={<PortalOnly><CustomerPortal /></PortalOnly>} />
            <Route path="/portal/tickets/:id" element={<PortalOnly><CustomerPortal /></PortalOnly>} />
            <Route path="/portal/documents" element={<PortalOnly><CustomerPortal view="documents" /></PortalOnly>} />

            <Route path="/" element={<P><HomeRouter /></P>} />
            <Route path="/bids" element={<A><Bids /></A>} />
            <Route path="/bid-wizard" element={<A><BidWizard /></A>} />
            <Route path="/pipeline" element={<A><Pipeline /></A>} />
            <Route path="/feeds/:source" element={<A><Feed /></A>} />
            <Route path="/documents" element={<A><Documents /></A>} />
            <Route path="/proposals" element={<A><Proposals /></A>} />
            <Route path="/assistant" element={<A><Assistant /></A>} />
            <Route path="/emails" element={<A><Emails /></A>} />
            <Route path="/email-templates" element={<A><EmailTemplates /></A>} />
            <Route path="/team" element={<A><Team /></A>} />
            <Route path="/hr" element={<P><HRPayroll /></P>} />
            <Route path="/profile" element={<P><Profile /></P>} />
            <Route path="/dms/:kind" element={<A><DMS /></A>} />
            <Route path="/suppliers" element={<A><Suppliers /></A>} />
            <Route path="/price-list" element={<A><PriceList /></A>} />
            <Route path="/tickets" element={<A><Tickets /></A>} />
            <Route path="/company" element={<A><CompanySettings /></A>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}
